import { useState, useMemo, useCallback } from 'react';
import { Shield, CheckCircle, XCircle, AlertTriangle, Play, Loader, Database, HardDrive } from 'lucide-react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getBytes } from 'firebase/storage';
import { db, storage } from '../../../config/firebase';
import { colors, Card, Button } from '../../shared';
import {
  ROLES,
  GOD_MODE_EMAILS,
  FIRESTORE_PERMISSIONS,
  STORAGE_PERMISSIONS,
  COLLECTION_CATEGORIES,
  getCollectionsByCategory,
  getStoragePathsByCategory,
} from '../../../data/permissions';

// ============================================
// ACCESS BADGE COMPONENT
// ============================================
const AccessBadge = ({ read, write }) => {
  if (read && write) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
        style={{ background: colors.greenDim, color: colors.green }}>
        R/W
      </span>
    );
  }
  if (read) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
        style={{ background: 'rgba(96, 165, 250, 0.15)', color: '#60A5FA' }}>
        Read
      </span>
    );
  }
  if (write) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
        style={{ background: colors.orangeDim, color: colors.orange }}>
        Write
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
      style={{ background: colors.redDim, color: colors.red }}>
      None
    </span>
  );
};

// ============================================
// VERIFICATION RESULT BADGE
// ============================================
const VerifyBadge = ({ status }) => {
  if (status === 'pass') {
    return <CheckCircle size={14} style={{ color: colors.green }} />;
  }
  if (status === 'fail') {
    return <XCircle size={14} style={{ color: colors.red }} />;
  }
  if (status === 'running') {
    return <Loader size={14} className="animate-spin" style={{ color: colors.yellow }} />;
  }
  return null;
};

// ============================================
// PERMISSIONS MATRIX TABLE
// ============================================
const PermissionsMatrix = ({ title, icon: Icon, data, verifyResults }) => {
  const roles = Object.keys(ROLES);

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} style={{ color: colors.yellow }} />
        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
          {title}
        </h3>
      </div>

      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: colors.cardBg }}>
              <th className="text-left px-4 py-3 font-medium" style={{ color: colors.textSecondary }}>
                Resource
              </th>
              {roles.map(role => (
                <th key={role} className="text-center px-3 py-3 font-medium" style={{ color: colors.textSecondary }}>
                  {ROLES[role].label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([category, group]) => (
              <>
                <tr key={`cat-${category}`}>
                  <td colSpan={roles.length + 1} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                    style={{ background: 'rgba(226, 255, 46, 0.05)', color: colors.yellow }}>
                    {group.label}
                  </td>
                </tr>
                {group.items.map(item => (
                  <tr key={item.id} className="border-t" style={{ borderColor: colors.border }}>
                    <td className="px-4 py-2.5 font-medium" style={{ color: colors.textPrimary }}>
                      <div className="flex items-center gap-2">
                        <span>{item.label}</span>
                        {verifyResults?.[item.id] && (
                          <VerifyBadge status={verifyResults[item.id].status} />
                        )}
                      </div>
                      <span className="text-xs" style={{ color: colors.textMuted }}>{item.id}</span>
                    </td>
                    {roles.map(role => {
                      const access = item.access[role];
                      const verifyKey = `${item.id}:${role}`;
                      return (
                        <td key={role} className="text-center px-3 py-2.5">
                          <div className="flex flex-col items-center gap-1">
                            <AccessBadge read={access.read} write={access.write} />
                            {verifyResults?.[verifyKey] && (
                              <VerifyBadge status={verifyResults[verifyKey].status} />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// MAIN PERMISSIONS TAB
// ============================================
export default function PermissionsTab() {
  const [verifying, setVerifying] = useState(false);
  const [verifyResults, setVerifyResults] = useState(null);
  const [verifyError, setVerifyError] = useState(null);

  // Group Firestore collections by category for display
  const firestoreData = useMemo(() => {
    const grouped = {};
    for (const [collectionId, config] of Object.entries(FIRESTORE_PERMISSIONS)) {
      const cat = config.category;
      if (!grouped[cat]) {
        grouped[cat] = {
          label: COLLECTION_CATEGORIES[cat].label,
          items: [],
        };
      }
      grouped[cat].items.push({ id: collectionId, ...config });
    }
    return grouped;
  }, []);

  // Group Storage paths by category for display
  const storageData = useMemo(() => {
    const grouped = {};
    for (const [path, config] of Object.entries(STORAGE_PERMISSIONS)) {
      const cat = config.category;
      if (!grouped[cat]) {
        grouped[cat] = {
          label: COLLECTION_CATEGORIES[cat].label,
          items: [],
        };
      }
      grouped[cat].items.push({ id: path, ...config });
    }
    return grouped;
  }, []);

  // ============================================
  // LIVE VERIFICATION
  // ============================================
  const runVerification = useCallback(async () => {
    setVerifying(true);
    setVerifyError(null);
    const results = {};

    try {
      // Test Firestore read access for each collection
      for (const [collectionId, config] of Object.entries(FIRESTORE_PERMISSIONS)) {
        const key = collectionId;
        results[key] = { status: 'running' };
        setVerifyResults({ ...results });

        try {
          await getDocs(collection(db, collectionId));
          results[key] = { status: 'pass', detail: 'Read succeeded' };
        } catch (err) {
          if (err.code === 'permission-denied') {
            results[key] = { status: 'fail', detail: 'Read denied by rules' };
          } else {
            results[key] = { status: 'pass', detail: `Read OK (${err.code || 'empty'})` };
          }
        }
      }

      // Test Firestore write access with a dry-run doc
      for (const [collectionId, config] of Object.entries(FIRESTORE_PERMISSIONS)) {
        const writeKey = `${collectionId}:write`;
        results[writeKey] = { status: 'running' };
        setVerifyResults({ ...results });

        const testDocId = `_permissions_verify_${Date.now()}`;
        try {
          await setDoc(doc(db, collectionId, testDocId), {
            _test: true,
            _createdBy: 'permissions-verify',
            _timestamp: new Date().toISOString(),
          });
          // Clean up test doc
          try {
            await deleteDoc(doc(db, collectionId, testDocId));
          } catch {
            // Cleanup failure is non-critical
          }
          results[writeKey] = { status: 'pass', detail: 'Write succeeded' };
        } catch (err) {
          if (err.code === 'permission-denied') {
            results[writeKey] = { status: 'pass', detail: 'Write correctly denied' };
          } else {
            results[writeKey] = { status: 'fail', detail: `Unexpected error: ${err.code}` };
          }
        }
      }

      // Test Storage read/write for each path
      for (const [path, config] of Object.entries(STORAGE_PERMISSIONS)) {
        const key = `storage:${path}`;
        results[key] = { status: 'running' };
        setVerifyResults({ ...results });

        const testPath = `${path}_permissions_verify_${Date.now()}.txt`;
        const testData = new Uint8Array([0x74, 0x65, 0x73, 0x74]); // "test"

        try {
          await uploadBytes(ref(storage, testPath), testData);
          results[key] = { status: 'pass', detail: 'Storage write succeeded' };
        } catch (err) {
          if (err.code === 'storage/unauthorized') {
            results[key] = { status: 'fail', detail: 'Storage write denied' };
          } else {
            results[key] = { status: 'pass', detail: `OK (${err.code || 'unknown'})` };
          }
        }
      }

      setVerifyResults({ ...results });
    } catch (err) {
      setVerifyError(err.message);
    } finally {
      setVerifying(false);
    }
  }, []);

  // Compute summary stats from results
  const verifySummary = useMemo(() => {
    if (!verifyResults) return null;
    const entries = Object.values(verifyResults);
    return {
      total: entries.length,
      passed: entries.filter(r => r.status === 'pass').length,
      failed: entries.filter(r => r.status === 'fail').length,
      running: entries.filter(r => r.status === 'running').length,
    };
  }, [verifyResults]);

  return (
    <div className="p-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: colors.textPrimary }}>
            <Shield size={24} style={{ color: colors.yellow }} />
            Permissions Matrix
          </h2>
          <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
            Read-only view of role-based access control. Source of truth: <code className="px-1 rounded" style={{ background: colors.inputBg }}>src/data/permissions.js</code>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {verifySummary && (
            <div className="flex items-center gap-2 text-sm">
              <span style={{ color: colors.green }}>{verifySummary.passed} passed</span>
              {verifySummary.failed > 0 && (
                <span style={{ color: colors.red }}>{verifySummary.failed} failed</span>
              )}
            </div>
          )}
          <button
            onClick={runVerification}
            disabled={verifying}
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all"
            style={{
              background: verifying ? colors.inputBg : colors.yellow,
              color: verifying ? colors.textMuted : colors.black,
              opacity: verifying ? 0.7 : 1,
            }}
          >
            {verifying ? (
              <><Loader size={16} className="animate-spin" /> Verifying...</>
            ) : (
              <><Play size={16} /> Run Verification</>
            )}
          </button>
        </div>
      </div>

      {/* Verify Error */}
      {verifyError && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-2" style={{ background: colors.redDim }}>
          <AlertTriangle size={16} style={{ color: colors.red }} />
          <span className="text-sm" style={{ color: colors.red }}>{verifyError}</span>
        </div>
      )}

      {/* GOD MODE info */}
      <div className="mb-6 p-4 rounded-xl border" style={{ background: colors.cardBg, borderColor: colors.border }}>
        <h3 className="text-sm font-semibold mb-1" style={{ color: colors.yellow }}>GOD MODE</h3>
        <p className="text-sm" style={{ color: colors.textSecondary }}>
          The following emails bypass all restrictions and have full admin access:
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {GOD_MODE_EMAILS.map(email => (
            <span key={email} className="px-2 py-1 rounded text-xs font-mono"
              style={{ background: colors.yellowDim, color: colors.yellow }}>
              {email}
            </span>
          ))}
        </div>
      </div>

      {/* Role Descriptions */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(ROLES).map(([roleId, role]) => (
          <div key={roleId} className="p-3 rounded-xl border" style={{ background: colors.cardBg, borderColor: colors.border }}>
            <h4 className="text-sm font-semibold mb-1" style={{ color: colors.textPrimary }}>
              {role.label}
            </h4>
            <p className="text-xs" style={{ color: colors.textMuted }}>
              {role.description}
            </p>
          </div>
        ))}
      </div>

      {/* Firestore Permissions Matrix */}
      <PermissionsMatrix
        title="Firestore Collections"
        icon={Database}
        data={firestoreData}
        verifyResults={verifyResults}
      />

      {/* Storage Permissions Matrix */}
      <PermissionsMatrix
        title="Storage Paths"
        icon={HardDrive}
        data={storageData}
        verifyResults={verifyResults}
      />

      {/* Legend */}
      <div className="mt-6 p-4 rounded-xl border" style={{ background: colors.cardBg, borderColor: colors.border }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.textSecondary }}>
          Legend
        </h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <AccessBadge read={true} write={true} />
            <span style={{ color: colors.textMuted }}>Full access</span>
          </div>
          <div className="flex items-center gap-2">
            <AccessBadge read={true} write={false} />
            <span style={{ color: colors.textMuted }}>Read only</span>
          </div>
          <div className="flex items-center gap-2">
            <AccessBadge read={false} write={true} />
            <span style={{ color: colors.textMuted }}>Write only</span>
          </div>
          <div className="flex items-center gap-2">
            <AccessBadge read={false} write={false} />
            <span style={{ color: colors.textMuted }}>No access</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle size={14} style={{ color: colors.green }} />
            <span style={{ color: colors.textMuted }}>Verified</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle size={14} style={{ color: colors.red }} />
            <span style={{ color: colors.textMuted }}>Verification failed</span>
          </div>
        </div>
      </div>

      {/* Implementation Notes */}
      <div className="mt-4 p-4 rounded-xl border" style={{ background: colors.cardBg, borderColor: colors.border }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: colors.textSecondary }}>
          Implementation Notes
        </h3>
        <ul className="text-xs space-y-1" style={{ color: colors.textMuted }}>
          <li>Firestore rules enforce role checks by reading the user's profile from <code>/users/{'{uid}'}</code>.</li>
          <li>Storage rules use custom auth claims for role checks. Without claims, all @snshn.co users have full access (fallback mode).</li>
          <li>GOD MODE (aj@snshn.co) bypasses all role checks at the rules level.</li>
          <li>Users can always write their own <code>/users/{'{uid}'}</code> document (for login timestamps, etc.).</li>
          <li>Verification runs as the currently logged-in user — results reflect YOUR role's access.</li>
        </ul>
      </div>
    </div>
  );
}
